import React, { useState, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Nullable } from "@jamesgmarks/utilities";
import { ISubscriptions } from "@llws/lift-entity-interfaces";
import {
  ActiveFeatureSchema,
  CustomConfigSchema,
  TCustomConfiguration,
} from "./customConfigMeta";
import { JsonRenderer } from "src/components/pages/Subscriptions/EditorComponents/JsonRenderer";

interface IActiveFeatureConfigurationEditorProps {
  config: Pick<TCustomConfiguration, "activeFeature">;
  onSave: (updatedConfig: Pick<TCustomConfiguration, "activeFeature">) => void;
  onCancel: () => void;
}

export const ActiveFeatureConfigurationEditor: React.FC<IActiveFeatureConfigurationEditorProps> = ({
  config,
  onSave,
  onCancel,
}) => {
  const [productKey, setProductKey] = useState(config.activeFeature?.productKey ?? "");

  const validationError = useMemo(() => {
    const result = ActiveFeatureSchema.safeParse({ productKey });
    return result.success ? null : "Product key is required.";
  }, [productKey]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="body1" mt={2}><u>Active Feature Product Key</u></Typography>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Product Key"
          value={productKey}
          onChange={(e) => setProductKey(e.target.value)}
        />
      </Grid>

      {validationError && (
        <Grid item xs={12}>
          <Alert severity="warning">{validationError}</Alert>
        </Grid>
      )}

      <Grid item container spacing={2} justifyContent="center" mt={2}>
        <Grid item>
          <Button variant="contained" color="error" onClick={onCancel}>
            Cancel
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="success"
            disabled={!!validationError}
            onClick={() => onSave({ activeFeature: { productKey } })}
          >
            Update
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

interface IActiveFeatureConfigurationAccordionProps {
  isEditingSubscriptionConfiguration: boolean;
  setIsEditingSubscriptionConfiguration: React.Dispatch<React.SetStateAction<boolean>>;
  baseSubscriptionConfiguration: Nullable<TCustomConfiguration>;
  subscriptionConfiguration: Nullable<TCustomConfiguration>;
  setCustomConfiguration: (config: TCustomConfiguration) => void;
  storeSubscription: Nullable<ISubscriptions>;
  hasErrors?: boolean;
}

export const ActiveFeatureConfigurationAccordion: React.FC<IActiveFeatureConfigurationAccordionProps> = ({
  isEditingSubscriptionConfiguration,
  setIsEditingSubscriptionConfiguration,
  baseSubscriptionConfiguration,
  subscriptionConfiguration,
  setCustomConfiguration,
  storeSubscription,
}) => {
  const onCopyBaseSubscriptionConfiguration = () => {
    const baseConfig = CustomConfigSchema.safeParse(baseSubscriptionConfiguration);
    if (baseConfig.success) {
      setCustomConfiguration(baseConfig.data);
    }
  };

  const onCopyOriginalSubscriptionConfiguration = () => {
    const config = CustomConfigSchema.safeParse(storeSubscription?.customConfiguration);
    if (config.success) {
      setCustomConfiguration(config.data);
    }
  };

  return (
    <Grid container spacing={1} textAlign="center">
      <Grid item xs={isEditingSubscriptionConfiguration ? 5 : 6}>
        <Typography variant="h6" textAlign="center" mb={2}>
          Base subscription configuration
        </Typography>

        {baseSubscriptionConfiguration?.activeFeature ? (
          <JsonRenderer
            obj={baseSubscriptionConfiguration.activeFeature}
            keyReplacements={{ productKey: "Product Key" }}
          />
        ) : (
          <Alert icon={false} color="warning" sx={{ justifyContent: "center" }}>
            <i>No active feature set in base subscription.</i>
          </Alert>
        )}
      </Grid>

      <Grid item xs={isEditingSubscriptionConfiguration ? 7 : 6}>
        <Typography variant="h6" textAlign="center" mb={2}>
          <b>Subscription configuration to save...</b>
        </Typography>

        {isEditingSubscriptionConfiguration && subscriptionConfiguration ? (
          <ActiveFeatureConfigurationEditor
            config={{ activeFeature: subscriptionConfiguration.activeFeature }}
            onSave={(updatedConfig) => {
              const config = {
                ...subscriptionConfiguration,
                activeFeature: updatedConfig.activeFeature,
              };

              setCustomConfiguration(config);
              setIsEditingSubscriptionConfiguration(false);
            }}
            onCancel={() => setIsEditingSubscriptionConfiguration(false)}
          />
        ) : subscriptionConfiguration?.activeFeature ? (
          <Box>
            <JsonRenderer
              obj={subscriptionConfiguration.activeFeature}
              backgroundColor="#e7fce5"
              keyReplacements={{ productKey: "Product Key" }}
            />
            <Grid item xs={12} mt={2}>
              <Button
                fullWidth
                variant="contained"
                color="warning"
                onClick={() => setIsEditingSubscriptionConfiguration(true)}
              >
                Edit
              </Button>
            </Grid>
          </Box>
        ) : (
          <Alert icon={false} color="warning" sx={{ justifyContent: "center" }}>
            <i>No active feature set for subscription.</i>
          </Alert>
        )}

        <Grid item xs={12} mt={2}>
          <Button
            fullWidth
            variant="contained"
            onClick={onCopyOriginalSubscriptionConfiguration}
            disabled={!storeSubscription?.customConfiguration?.activeFeature}
          >
            Use last-saved subscription configuration
          </Button>
        </Grid>

        <Grid item xs={12} my={2}>
          <Button
            fullWidth
            variant="contained"
            onClick={onCopyBaseSubscriptionConfiguration}
            disabled={!baseSubscriptionConfiguration?.activeFeature}
          >
            Copy base subscription configuration
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};
